import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import PageSeo from '../../components/PageSeo';
import { Construction, FileText, Wrench, Users, ChevronRight, Building2, MapPin } from 'lucide-react';

// Icon mapping for families
const FAMILY_ICONS = {
  'travaux-fondations-speciales': Construction,
  'ingenierie-etudes-geotechniques': FileText,
  'materiel-equipements-forage': Wrench,
  'conseil-recrutement-freelance': Users
};

const FAMILY_COLORS = {
  'travaux-fondations-speciales': 'from-blue-600 to-blue-800',
  'ingenierie-etudes-geotechniques': 'from-green-600 to-green-800',
  'materiel-equipements-forage': 'from-orange-500 to-orange-700',
  'conseil-recrutement-freelance': 'from-purple-600 to-purple-800'
};

// Static data for SSG pre-rendering
const STATIC_FAMILIES = [
  {
    id: 'travaux-fondations',
    slug: 'travaux-fondations-speciales',
    name_fr: 'Travaux de Fondations Spéciales',
    description_fr: 'Entreprises spécialisées dans les travaux de fondations spéciales : micropieux, pieux forés, parois moulées, tirants, injections.',
    category_count: 10,
    professional_count: 0
  },
  {
    id: 'ingenierie',
    slug: 'ingenierie-etudes-geotechniques',
    name_fr: 'Ingénierie & Études Géotechniques',
    description_fr: 'Bureaux d\'études géotechniques, laboratoires d\'essais sols et entreprises d\'instrumentation.',
    category_count: 6,
    professional_count: 0
  },
  {
    id: 'materiel',
    slug: 'materiel-equipements-forage',
    name_fr: 'Matériel & Équipements de Forage',
    description_fr: 'Achat, vente et location de foreuses, machines d\'essais, outils de forage et équipements.',
    category_count: 6,
    professional_count: 0
  },
  {
    id: 'conseil',
    slug: 'conseil-recrutement-freelance',
    name_fr: 'Conseil, Recrutement & Freelance',
    description_fr: 'Consultants indépendants, ingénieurs freelance et cabinets de recrutement spécialisés.',
    category_count: 3,
    professional_count: 0
  }
];

export default function DirectoryMain() {
  const { API } = useContext(AppContext);
  // Use static data as initial state for SSG
  const [families, setFamilies] = useState(STATIC_FAMILIES);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ totalProfessionals: 0, totalCategories: 25 });

  useEffect(() => {
    const fetchFamilies = async () => {
      try {
        const response = await axios.get(`${API}/directory/families`);
        setFamilies(response.data);
        
        // Calculate stats
        let totalPros = 0;
        let totalCats = 0;
        for (const family of response.data) {
          totalPros += family.professional_count || 0;
          totalCats += family.category_count || 0;
        }
        setStats({ totalProfessionals: totalPros, totalCategories: totalCats });
      } catch (error) {
        console.error('Error fetching families:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFamilies();
  }, [API]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PageSeo 
        title="Annuaire Géotechnique | Entreprises Fondations Spéciales France & Afrique"
        description="Trouvez les entreprises de fondations spéciales, bureaux d'études géotechniques, loueurs de foreuses et consultants. Annuaire professionnel GEOTS."
        canonical="/annuaire"
      />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Building2 className="w-4 h-4 text-yellow-400" />
              <span className="text-sm font-medium">Annuaire professionnel spécialisé</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Annuaire des professionnels
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                géotechnique & fondations
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl">
              Trouvez les entreprises spécialisées en fondations spéciales, bureaux d'études, loueurs de matériel et consultants indépendants.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                <MapPin className="w-5 h-5 text-green-400" />
                <span>France & Afrique</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-400" />
                <span>{stats.totalCategories} spécialités</span>
              </div>
            </div>
            
            <Link to="/directory/submit">
              <Button size="lg" className="bg-gradient-to-r from-[#635BFF] to-[#8B5CF6] hover:from-[#5248d9] hover:to-[#7C3AED] text-lg px-8 py-6 shadow-xl shadow-purple-500/20">
                + Inscrire mon entreprise gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Families Grid */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-4">
            Explorez par domaine d'activité
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Sélectionnez un domaine pour découvrir les sous-catégories et trouver les professionnels correspondants.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="animate-pulse bg-gray-200 rounded-2xl h-64"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {families.map(family => {
              const IconComponent = FAMILY_ICONS[family.slug] || Building2;
              const gradientClass = FAMILY_COLORS[family.slug] || 'from-gray-600 to-gray-800';
              
              return (
                <Link 
                  key={family.id} 
                  to={`/annuaire/${family.slug}`}
                  className="group"
                >
                  <Card className="h-full overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className={`bg-gradient-to-br ${gradientClass} p-8 text-white`}>
                      <div className="flex items-start justify-between mb-6">
                        <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                          <IconComponent className="w-10 h-10" />
                        </div>
                        <ChevronRight className="w-6 h-6 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-2 transition-all" />
                      </div>
                      
                      <h3 className="text-2xl font-bold mb-3">{family.name_fr}</h3>
                      <p className="text-white/80 mb-6 line-clamp-2">{family.description_fr}</p>
                      
                      <div className="flex items-center gap-4 text-sm">
                        <span className="bg-white/20 px-3 py-1 rounded-full">
                          {family.category_count || 0} spécialités
                        </span>
                        <span className="bg-white/20 px-3 py-1 rounded-full">
                          {family.professional_count || 0} entreprises
                        </span>
                      </div>
                    </div>
                    
                    <CardContent className="p-6 bg-white">
                      <p className="text-gray-600 text-sm">
                        Cliquez pour voir les {family.category_count || 'différentes'} sous-catégories →
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-[#635BFF] to-[#8B5CF6] py-16">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Vous êtes un professionnel du secteur ?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Inscrivez votre entreprise gratuitement dans notre annuaire et gagnez en visibilité auprès des donneurs d'ordre.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/directory/submit">
              <Button size="lg" className="bg-white text-[#635BFF] hover:bg-gray-100 text-lg px-8">
                Inscrire mon entreprise
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
